import type { ICustomTheme } from '@engyalo/delivery-ui-components/lib/interfaces/theme'
import type {
  ItemCardContentProps,
  ItemCardProps,
} from '@engyalo/delivery-ui-components/lib/ui/components/ItemCard'
import type { CartListProps } from '@engyalo/delivery-ui-components/lib/ui/components/CartList'
import type { ItemListProps } from '@engyalo/delivery-ui-components/lib/ui/components/ItemList'
import type { SnackbarProps } from '@engyalo/delivery-ui-components/lib/ui/components/Snackbar/Snackbar.types'
import type { SearchBarProps } from '@engyalo/delivery-ui-components'
import type { PriceProps } from '@engyalo/delivery-ui-components/lib/ui/components/Price'
import type { SessionDataArgs } from '../graphQL/queries/getSession/types'
import type { ICinnamonActions, IDialogDetails } from './actions'
import type DEFAULT_COMPONENTS_PROPS from '../consts/defaultConfigValues/defaultProps'
import type { IPackageProduct, IPlaceOrderFlow, UnresolvedRecord } from '.'

export interface IRoutes {
  route: string
  container: string
  exact?: boolean
}

export interface IComponentActionsProps {
  propName: string
  name: string
  params: UnresolvedRecord
}

export interface Icomponent {
  uid: string
  name: string
  importRoute: string
  defaultProps?: keyof typeof DEFAULT_COMPONENTS_PROPS
  props?: UnresolvedRecord & {
    actions?: Array<IComponentActionsProps>
    runOnLoad?: ICinnamonActions
    hideOnScroll?: boolean
  }
  isExternal?: boolean
}

export type IProcessedComponentProps<T> = UnresolvedRecord &
  DefaultProps<T> & {
    runOnLoad?: (...args: unknown[]) => void
    reachBottomActions?: (...args: unknown[]) => void
  }
export interface IProcessedComponent<T = unknown> {
  uid: string
  name: string
  importRoute: string
  defaultProps?: keyof typeof DEFAULT_COMPONENTS_PROPS
  props?: IProcessedComponentProps<T>
}

export interface IContainerSections {
  components: Array<Icomponent>
  props?: UnresolvedRecord
}

export interface IProcessedContainerSections<T = unknown, U = unknown> {
  components: Array<IProcessedComponent<T>>
  props?: IProcessedComponentProps<U>
}

export interface IContainersBase {
  uid: string
  type?:
    | 'cart'
    | 'catalog'
    | 'catalogFixed'
    | 'categories'
    | 'filters'
    | 'summary'
    | 'categoriesnav'
    | 'productdetail'
    | 'orderHistory'
    | 'orderDetail'
    | 'orderConfirmation'
    | 'deliveryDatePicker'
    | 'paymentMethodSelector'
  title: string
  slug: string
  // @Todo: Fixme! We should know the type for this
  header?: IContainerSections
  body: IContainerSections
  footer?: IContainerSections
  skipDefaults?: boolean
}

export interface IModal extends IContainersBase {
  props?: UnresolvedRecord
}

export interface IModalsObject extends Record<string, IModal> {}

export interface IContainers extends IContainersBase {
  modals?: Array<string>
}

export interface IProcessedContainer
  extends Pick<IContainers, 'uid' | 'modals'> {
  header: IProcessedContainerSections
  body: IProcessedContainerSections
  footer: IProcessedContainerSections
}

export interface IContainersObject extends Record<string, IContainers> {}

/**
 * One group of texts, e.g. `texts.cart` or `texts.orders`.
 *
 * A leaf is a string, **or one more level of strings**. The nesting is not
 * decorative: `texts.orders.detail.refreshLabel`, `texts.orders.status.CREATED`
 * and `texts.orderConfirmation.failed.title` are all real tokens, and
 * `processData` resolves a dotted path by walking objects
 * (`selectors.reduce((src, key) => src?.[key])`), so a flat `"detail.x"` key
 * would not resolve.
 *
 * The previous declaration allowed two levels only. It was already inaccurate
 * — those `orders` tokens have existed since the Order Detail screen shipped —
 * and nothing caught it because no `defaultTexts` file carried an `orders` key,
 * so the shape never reached a type-checked file.
 *
 * Consequence for readers: a two-level access is now `string | ITextGroup`
 * rather than `string`. Call sites that only read flat keys go through
 * `flatTexts` rather than each carrying its own cast.
 */
export interface ITextGroup {
  [key: string]: string | { [key: string]: string }
}

export interface ITextLibrary {
  [key: string]: ITextGroup
}

export interface IMetatags {
  description?: string
  keywords?: string
  author?: string
  favicon?: string
  title?: string
  charset?: string
  lang?: string
}
export interface ITemplateLibrary {
  list: Record<string, string>
  components: {
    [name: string]: {
      [variant: string]: {
        [prop: string]: any
      }
    }
  }
}

export enum ErrorMessageTranslation {
  GetCart = 1,
  CatalogueEmptyData,
  InitialData,
  NonProvidedParams,
  ErrorOnInitialLoad,
}

export interface IAnalytics {
  googleAnalyticsId: string
  fbPixelId: string
}

/**
 * Package field the multipackage rows may be ordered by.
 *
 * A deliberate subset of `IPackageProduct`, written with `Pick` so that
 * renaming or removing one of these package fields fails to compile here, at
 * the declaration, rather than at the call site.
 *
 * Excluded on purpose:
 * - `stock` is live, per-customer data. Ordering by it would flip as stock
 *   depletes and differ between two shoppers looking at the same product, which
 *   is the very inconsistency this option exists to remove.
 * - `maxQty`, `minQty` and `jumpQty` are nullable, and `null` coerces to `0` in
 *   a `<` / `>` comparison, so a package with no limit would lead the ascending
 *   order as though its limit were zero.
 * - `suffix` is a formatted price string, and `value` is the shopper's own cart
 *   quantity; neither describes the package.
 */
export type PackageSortField = keyof Pick<
  IPackageProduct,
  'quantity' | 'price' | 'label' | 'name'
>

/**
 * Renders the multipackage rows in a fixed order instead of the order the
 * catalog returns them in.
 *
 * The catalog order is whatever each product document happens to store, so it
 * varies from product to product: some list the unit package first, others the
 * box. Customers add quantity to whichever row is on top, so an inconsistent
 * order makes them buy boxes when they meant units.
 *
 * Off by default; packages keep catalog order unless a storefront opts in.
 *
 * These values arrive as remote JSON, so the types below are authoring
 * guidance rather than a runtime guard. Anything unrecognised — a misspelled
 * `field`, a `direction` that is neither `asc` nor `desc`, an `enabled` that
 * isn't a boolean — leaves the packages in catalog order and reports once when
 * the config loads, rather than ordering by a value nobody chose. `direction`
 * is matched case-insensitively; `field` is not.
 *
 * Applied in `sanitizeExtraPackages` through the shared `sortByKey` helper,
 * whose sort is stable, so packages with an equal key keep their catalog order.
 */
export type PackageSortConfig = {
  /**
   * Opt-in, and strictly the boolean `true`. Absent, `false`, or any
   * non-boolean leaves the catalog order untouched.
   */
  enabled?: boolean
  /**
   * Defaults to `quantity` — units per package, which is what places the single
   * unit above the box.
   *
   * `label` and `name` compare by UTF-16 code unit rather than by locale, so
   * case dominates: every capitalised label sorts ahead of every lowercase one
   * ("Unidad" before "caja chica"). A non-ASCII character sorts after every
   * ASCII letter, but only when it falls at the first differing position —
   * "Cajón" still sorts next to "Caja", not last.
   */
  field?: PackageSortField
  /** Defaults to `asc`. Matched case-insensitively. */
  direction?: 'asc' | 'desc'
}

export interface IGlobals {
  searchBar?: {
    searchTermMinLength?: SearchBarProps['searchTermMinLength']
    searchTermsExceptions?: SearchBarProps['searchTermsExceptions']
    searchTimeout?: SearchBarProps['searchTimeout']
  }
  navMenu?: 'tabs' | 'dropdown'
  counter?: {
    step?: number
    activeInput?: boolean
    max?: number | null
    modifierMap?: Record<string, number>
    allowRetryWhenOutOfStock?: boolean
    clampTypedInputToCap?: boolean
    allowTypedZero?: boolean
    /**
     * Treats a field the customer emptied as a typed zero, so the gesture that
     * ends the edit removes the line.
     *
     * **Requires `allowTypedZero`, and the library enforces that** rather than
     * merely documenting it: with that flag off this one holds nothing and
     * commits nothing, so enabling it alone is inert.
     *
     * Exists because `allowTypedZero` alone is unreachable on the platform this
     * webview mostly runs on. The counter renders `<input type="number">`
     * outside a `<form>`, which on Android raises a numeric keypad whose action
     * key moves focus and dispatches no keyboard event at all — so before
     * `@engyalo/delivery-ui-components@1.21.2` the only commit gesture was an
     * Enter key that does not exist there, and a customer who cleared the field
     * was left with a blank box over a quantity still being charged. 1.21.2
     * makes blur a commit gesture too, exempting the blurs that are not a
     * decision (a pointer gesture that began inside the counter, and the window
     * losing focus).
     *
     * ⚠️ **Destructive, with no confirmation of its own.** Clearing a quantity
     * and tapping away drops the line. Bound on `CounterDefault`, so it reaches
     * all eight counter variants; a storefront that wants it in only some
     * places overrides the prop per variant in its SCS config rather than
     * setting this global.
     *
     * Default `false`, per the zero-surprises invariant `allowTypedZero`
     * follows — no storefront changes behaviour until it authors this key.
     */
    allowEmptyInputAsZero?: boolean
    /**
     * Disables a product's Counters while an add, decrement or delete for it
     * is in flight, so the displayed quantity resyncs to the real one when the
     * server answers. The resync fires on the `true -> false` edge, i.e. when
     * the mutation settles, which is what makes a rejected change fall back to
     * the allowed quantity instead of showing what the user typed. Covers the
     * product detail Counter and the list rows of the slices that handle
     * `setSkuSyncing` (`redux/skuSyncing.ts`); list rows need a library whose
     * ItemCard reads `item.isSyncing` (1.23.1 does).
     *
     * With this on, each add or remove request is also aborted after
     * `CART_MUTATION_TIMEOUT_MS` (15 s, `utils/cartMutationTimeout.ts`), since
     * a request that never settles would otherwise leave the rows disabled
     * with no signal. A timeout is treated as a failure: it is reported to
     * Datadog as `cart mutation timed out`, shows the network alert, refetches
     * the cart and clears the mark. With this off no timer is armed.
     *
     * Leave this off unless the installed `@engyalo/delivery-ui-components`
     * actually ignores `+` / `-` clicks while the Counter is disabled. 1.23.1
     * does, with one exception: a `+` press at the max still runs the
     * disabled branch and fires `disabledActions` (a `-` at the min runs the
     * same branch, but the minus button is given no `disabledActions`). In
     * versions that only dim it, the click still goes through: the second tap
     * aborts the in-flight request, the abort settles the flag, and the resync
     * snaps the display back to the last confirmed quantity mid-edit. That is
     * worse than the bug this fixes, and it also defeats cinnamon's own
     * abort-and-resend design, which exists so a user can keep incrementing
     * during a round trip.
     */
    disableWhileSyncing?: boolean
  }
  packages?: {
    sort?: PackageSortConfig
  }
  promotions?: {
    /**
     * Disables a combo or complex promotion's "add all" button on the product
     * page when what it would add cannot be fulfilled — a required product,
     * package or quantity out of stock, a fixed gift that cannot be delivered,
     * or a bundle that needs its options chosen first — and gives the reason
     * from `texts.promotions`. Also hides the button on a promotion with
     * several buys groups and several gets groups, which it cannot serve.
     *
     * Costs one extra `searchProduct` request per 20 SKUs those promotions
     * name, per product opened that shows an add-all button. A failed request
     * leaves every button enabled.
     *
     * ⚠️ Needs a `delivery-ui-components` that reads `buttonDisabled` and
     * `disabledReason` on a promotion shortcut. On an older one the button
     * stays drawn and pressable, and the add-all action refusing the click is
     * the only guard: the shopper presses it and nothing happens. Do not
     * enable this before the library bump.
     *
     * @default false
     */
    disableUnfulfillableAddAll?: boolean
  }
  catalog?: {
    layout?: ItemListProps['layout']
    bodyFields?: ItemCardContentProps['fields']
    footerFields?: ItemCardContentProps['fields']
    fieldsToDisplay?: ItemCardContentProps['fieldsToDisplay']
    /**
     * Let the catalogue lists skip re-rendering while none of their items and
     * none of their other props have changed by value.
     *
     * Cinnamon resolves component props from configuration on every state
     * change, so each render hands the list new objects and it re-renders even
     * when no product moved. Comparing by value instead removes almost all of
     * those renders; on a large catalogue it kept the cost of loading another
     * page flat with depth instead of growing with it.
     *
     * Off by default so existing storefronts keep their current render timing.
     *
     * Requires a `delivery-ui-components` whose `ItemList` consumes the prop.
     * On an older version it is **not** ignored: it falls through `ItemList`'s
     * rest props and nothing in the chain below them stops it, so it reaches
     * the DOM as an unknown attribute and React warns about it once per
     * catalogue list. The chain is spelled out where the binding lives
     * (`defaultProps/CatalogueList.ts`). Do not enable this, or merge the
     * binding, before the dependency is bumped.
     *
     * @default false
     */
    memoizeItems?: boolean
  }
  price?: {
    packagePriceMap?: Record<string, string>
  } & Partial<PriceProps>
  unitPrice?: {
    showUnitPrice?: boolean
    unitsSize?: number | string
    unitsColor?: string
    unitsInline?: boolean
  }
  minAmount?: {
    showAmount?: boolean
    showIndicator?: boolean
  }
  tags?: ItemCardProps['tags']
  sideContent?: ItemCardProps['sideContent']
  cartLabels?: CartListProps['labelMap']
  /**
   * Text-rendering options shared by the catalogue cards and the cart/summary
   * lists.
   */
  text?: {
    /**
     * How a product `description` string is turned into DOM. `'sanitized'`
     * (the shipped default) keeps formatting markup and strips active content
     * (scripts, event handlers, frames, inline styles). `'html'` renders the
     * string verbatim; it is unsafe for tenant-supplied descriptions and
     * exists only as an escape hatch for a storefront that must keep raw HTML.
     * Requires an `@engyalo/delivery-ui-components` that supports the
     * `descriptionFormat` prop; older versions ignore it and render raw HTML.
     */
    descriptionFormat?: 'html' | 'sanitized'
  }
  imageConfig?: {
    resizeEnabled: boolean
    imageSizes: {
      cartImage: string
      catalogueImage: string
      suggestedItemImage: string
      productDetailImage: string
      searchImage: string
      filterImage: string
      frequentlyBoughtTogetherImage: string
    }
  }
  /**
   * How many products each recommendation placement asks the API for.
   *
   * One block for all four rather than two adjacent look-alike keys: a
   * storefront author told to "raise the recommendations limit" should not
   * have to know which carousel is called what, and guessing wrong would
   * merge cleanly into a real global that tunes a different placement.
   *
   * Every one of these is sent as the GraphQL `$limit: Int`. `carouselLimit`
   * and `listLimit` are normalised at config load — a value that is not a
   * positive integer is replaced by the shipped default and reported to
   * Datadog. The two legacy keys are deliberately left unnormalised, since
   * changing what they accept would change what existing storefronts render.
   */
  recommendedProducts: {
    /** Legacy suggested-items "ver todos" list. */
    allRecommendations: number
    /** Legacy suggested-items carousel. */
    widgetHome: number
    /** Buy Again, Previously Purchased and Recommended For You carousels. */
    carouselLimit: number
    /** The full-page lists behind those carousels' header button. */
    listLimit: number
    /**
     * Let a failed legacy suggested-items load keep `status: 'ERROR'`.
     * Off by default: `useSuggestionItems` then ends every fetch on `'OK'`,
     * failures included, so an empty list shows its empty message. With it
     * on, an empty list after a failure shows `errorMessage`, which the
     * default suggested-products list does not author, so it renders blank
     * unless the storefront authors one. Read `=== true`.
     */
    keepSuggestedItemsErrorStatus?: boolean
  }
  prefillCart: {
    createOrder: boolean
    limit: number
  }
  /**
   * The cart footer's place-order queue, and the summary footer's.
   *
   * `actions` and `pushAfter` are the cart's, unchanged.
   *
   * ⚠️ **`summaryActions` is a separate list, and binding the summary footer
   * to `actions` instead would stall checkout.** Two of the three entries in
   * the cart's default list advance the queue from inside a modal —
   * `CART_GET_FREQUENTLY_BOUGHT_TOGETHER` opens `RECOMMENDED_PRODUCTS` and
   * waits for a button in it, and `LINKED_PRODUCTS_GET_ITEMS` does the same.
   * `defaultConfig.ts` authors `CartSummary.modals: []`, and
   * `templates/Default.tsx` renders only a container's own modal list, so
   * neither modal can appear on the summary and `nextPlaceOrderAction` is
   * never dispatched. A shopper with recommendations or linked products in
   * their cart would tap *Realizar pedido* and get nothing, for ever.
   *
   * So it defaults to `[CART_PLACE_ORDER]` alone, which is the step that runs
   * the alcohol and returnables validations and opens the confirm dialog. A
   * storefront may author more once it also authors the modals to render
   * them.
   */
  placeOrderFlow: Partial<IPlaceOrderFlow> & { summaryActions?: string[] }
  [otherProps: string]: any
}

export enum Localization {
  En = 'en',
  Es = 'es',
  Pt = 'pt',
  Zh = 'zh',
  Ar = 'ar',
  He = 'he',
  Th = 'th',
  EnUs = 'en-us',
  EsMx = 'es-mx',
  PtBr = 'pt-br',
  ZhCn = 'zh-cn',
}

/**
 * Hides all monetary values for customers whose session custom field matches.
 * `customField` is a dot-path into `customer.customFields` (e.g. `flags.hide`);
 * `flagValue` is compared via string coercion (so `true`/`1` match `"true"`/`"1"`).
 * All three fields are required to hide anything — `enabled: true` alone does
 * nothing, and there is no unconditional hide-all.
 */
export type HidePricesConfig = {
  enabled?: boolean
  customField?: string
  flagValue?: string
}

/**
 * Overwrites the backend min-order message with a {{percentage}} progress text.
 * Opt-in via `enabled` (default false); `audience` defaults to 'all' (every
 * shopper), or 'hidePrices' to gate it to the hide-prices cohort. `message` is
 * an optional inline override (else falls back to texts.cart.minAmountProgress).
 */
export type OverwriteMinAmountMessageConfig = {
  enabled?: boolean
  audience?: 'all' | 'hidePrices'
  message?: string
}

export interface IConfig {
  routes: IRoutes[]
  defaultRoute?: string
  phoneNumber: string
  containers: IContainersObject
  logo?: string
  modals?: IModalsObject
  theme: ICustomTheme
  name: string
  botSlug: string
  globalErrorMessage: string
  texts: ITextLibrary
  metatags?: IMetatags
  templates: ITemplateLibrary
  globals: IGlobals
  analytics?: IAnalytics
  /**
   * Which route serves each checkout rule, so a rejected checkout can send the
   * shopper to the screen that fixes it.
   *
   * A shopper who adds to cart without picking a delivery date gets a rule
   * rejection. Without this map that is a dead end: an error toast and a cart
   * that will not advance. With it, COM-950 reads the failing rule off
   * `ordersSlice.failedCheckoutRules` and navigates here.
   *
   * **Top-level, a sibling of `routes`, and deliberately not a key on
   * `configuration.checkoutRules`.** That map is a closed set of camelCase
   * keys, each with a handler, and an unhandled key disables the whole
   * storefront's rule enforcement (COM-736). The rule types this map is keyed
   * on are SCREAMING_SNAKE and belong to a different subsystem. Keeping them
   * apart by shape as well as by name is what stops the two being confused
   * again — it has already produced a wrong answer four times.
   *
   * **Every value is optional, and an absent entry is normal.** A storefront
   * that authors no screen for a rule type has no entry, and COM-950 falls
   * back to a no-op rather than navigating somewhere that does not exist.
   * A storefront that authors none of this behaves exactly as it does today.
   *
   * Values are route paths as `routes[].route` spells them, so
   * `'/:slug/:sessionId/checkout/delivery-date'` rather than a container name.
   * Nothing validates that the path is one the config actually registers.
   *
   * ⚠️ **Nothing reads this yet.** COM-947 types it and lets it through the
   * config-loading path; COM-950 is the consumer. It ships no default, so
   * `deepMerge` leaves it absent on every storefront that does not author one.
   */
  checkoutRuleRoutes?: Partial<Record<CheckoutRules, string>>
  /**
   * Dialogs a storefront may author. One member today, and the first
   * authorable dialog surface in the repo.
   *
   * ⚠️ **Not `ISliceDefault.dialogs`.** That one is the single dialog
   * currently on screen, written by the `openDialog` reducer. This one is
   * authored config, read by `getConfirmOrderDialogProps`. Adjacent names,
   * unrelated objects; `state.defaultSlice.config.dialogs` is this one.
   */
  dialogs?: IConfigDialogs
  options?: {
    localization?: Localization
    systemThemeMode?: boolean
    orderMinAmount?: number
    overwriteMinAmountMessage?: OverwriteMinAmountMessageConfig
    currency?: string
    currencySymbol?: string
    currencyCode?: string
    currencyPrefix?: string
    pagination?: boolean
    useModal?: boolean
    format?: string
    promotionPercentageFormat?: string
    currencyDisplay?: string
    hidePrices?: HidePricesConfig
    /** derived — set by the config slice, do not author */
    hidePricesActive?: boolean
    /**
     * Whether "Order again" replaces the cart instead of appending to it.
     *
     * Defaults to appending. Getting the default wrong the other way would
     * silently discard a cart the shopper was still building, so replacing is
     * something a storefront opts into.
     */
    reorderReplacesCart?: boolean
    /**
     * Whether the webview creates and confirms the order itself, instead of
     * handing the shopper back to the conversation for the flow to do it.
     *
     * **The default is today's behaviour, and that is the point of the
     * option.** Every storefront in the estate reaches checkout through
     * `useCreateOrderHook` → `postCreateOrder`, which POSTs the stephook and
     * then redirects to the channel; the flow creates the order afterwards, in
     * chat. Opting in moves creation into the webview
     * (`hooks/orders/useOrderConfirmation.ts`) and suppresses that redirect,
     * so a storefront that has not authored this must behave byte-identically
     * and must never call `createOrder`.
     *
     * Compared against literal `true` for the reason
     * `orderConfirmation.required` is: the config is remote JSON with no
     * boolean guarantee, and a quoted `"false"` is truthy.
     *
     * ⚠️ Authoring this **without** editing the storefront's Builder flow
     * breaks checkout for the shopper, per storefront. The usual symptom is
     * *not* two orders: while the webview's order is still `CREATED`,
     * `validateOrderCreated` rejects the flow's preorder step with a `409
     * ALREADY_EXIST`, which surfaces as an error in the conversation with the
     * cart already consumed. Genuine double creation needs the webview's order
     * to have left `CREATED` first — confirmed, cancelled or errored — so plan
     * for a broken flow, not for reconciling duplicates.
     *
     * ⚠️ Unusable on a storefront with an active order splitter
     * (`configuration.splitters.order`). `createOrder` and `confirmOrder` both
     * reject outright there, naming `createOrders`/`confirmOrders`, which
     * cinnamon does not implement. `useCreateOrder` detects the combination,
     * declines the opt-in, takes the legacy path and reports to Datadog — so
     * authoring it is inert rather than harmful, but it will not do what the
     * author intended.
     *
     * What confirmation then applies is not decided here: it is derived from
     * the status `createOrder` returns, with the parameters on
     * `configuration.order.confirmation` (a *session* config block, authored
     * separately). This option only decides who creates the order.
     */
    inWebviewOrderConfirmation?: boolean
    placeholderImage?: string
    usePaginatedCategories?: boolean
    /**
     * Use total in cart groups, if false show subtotal
     */
    cartGroupingTotals?: boolean
    /**
     * Show freegoods as subitem in cart
     */
    freegoodsAsSubitem?: boolean
    /**
     * Show freegods as group in cart
     */
    freegoodAsCartGroup?: boolean
    /**
     * Show freegoods combo promo origin in cart
     */
    showFreegoodsComboOrigin?: boolean
    /**
     * Render a fully-gifted TOTAL promotion line at its real per-unit price
     * (`price - discounts / quantity`, i.e. 0 for a gift) instead of the value
     * derived from `discountsNoCombo`, which nets to the full unit price when
     * every unit is free.
     *
     * Only applies when the whole line is gifted (`quantity === promotionalQty`);
     * a line carrying paid units of the same SKU is unaffected. The promotion's
     * `pricePerItem` is deliberately not used -- backends disagree on its unit,
     * returning a per-unit price on one storefront and the whole line's value on
     * another.
     *
     * Note this sets the unit *price*. Whether the line *total* also shows as
     * "$0.00" is up to the storefront's ItemCard config -- `cartCondensed` sets
     * `priceTotal.hideOnZero: true`, which hides a zero total unless the
     * storefront overrides it.
     */
    showTotalFreegoodZeroPrice?: boolean
    /**
     * Ignore subtotal on cart
     */
    ignoreSubtotal?: boolean
    /**
     * Allow non zero subtotal orders
     */
    allowNonZeroSubtotalOrders?: boolean
    /**
     * Show restrictions apply label on cart
     */
    hideCartRestrictions?: boolean
    splash?: {
      logo?: boolean | string
      label?: string
      hideIcon?: boolean
    }
    /**
     * Hide subcategories options
     */
    hideSubcategories?: boolean
    /**
     * Hide products on subcategories
     */
    hideSubCategoriesProducts?: boolean
    /**
     * shows image for subcategories list
     */
    showSubCategoriesImages?: boolean
    /**
     * Hide products on subcategories
     */
    productDetailView?: boolean
    /**
     * track eventsURL, works as feature flag and also as parameter for send events
     */
    trackEventsURL?: string | null
    /**
     * Pagination size for the catalog
     */
    paginationSize?: number
    /**
     * url for resizer service
     */
    resizeServiceUrl?: string
    /**
     * Force show hidden categories
     * @default false
     */
    showHiddenCategories?: boolean
    /**
     * fill the qty for search result based on the cart item
     */
    fillSearchQty?: boolean
    /**
     * Text direction for UI elements
     */
    textDir?: React.HTMLAttributes<HTMLDivElement>['dir']
    /**
     * Split promotion tag in catalog
     */
    splitPromotionTagCatalog?: boolean
    /**
     * Split promotion tag in recommended products
     */
    splitPromotionTagRecommendedProducts?: boolean
  }
}

export interface IModals
  extends Record<
    string,
    {
      show: boolean
      state?: UnresolvedRecord
      item?: string
    }
  > {}

/**
 * What a storefront authors to turn the `confirmOrder` dialog's detail rows
 * on, and how it words them.
 *
 * ⚠️ **Presence of the block is the opt-in.** Every member is optional, so
 * `{ confirmOrder: {} }` turns the rows on with fleet-default copy and no
 * payment map. `defaultConfig.ts` authors no `dialogs` key and `deepMerge`
 * leaves it absent, so the dialog stays exactly as it is on every storefront
 * that says nothing.
 *
 * Cinnamon owns the rows themselves — Total, Entrega, Pago, in that order,
 * with the Total row marked `containsPrice` and `emphasis`. The library's
 * `MessageDetailRow` is closed (`label`, `value`, `emphasis`,
 * `containsPrice`) and carries no key to match an authored row against a
 * value out of the store, so an authored row list would need a cinnamon-only
 * discriminator that no longer matches the library's type.
 */
export interface IConfirmOrderDialogConfig {
  /**
   * The tenant's word for each backend payment enum value, e.g.
   * `{ CASH: 'Efectivo contra entrega' }`.
   *
   * No default, for the reason `defaultProps/CartCheckoutPanel.ts` gives: a
   * wrong entry here would rename every storefront's payment method. An
   * unmapped method renders its raw enum value, which is visible and
   * diagnosable. A method mapped to `''` withdraws its row, deliberately —
   * which is why the lookup uses `??` rather than `||`.
   *
   * Values may be `{{texts.…}}` tokens; they resolve with the rest of the
   * dialog's props in `components/Dialog.tsx`.
   */
  paymentMethodLabels?: Partial<Record<string, string>>
  /** Block tint, and the two row colours. Palette paths or raw CSS colours. */
  backgroundColor?: string
  labelColor?: string
  valueColor?: string
}

export interface IConfigDialogs {
  confirmOrder?: IConfirmOrderDialogConfig
}

export interface IDialog {
  show: boolean
  id?: string
  icon: {
    name: string
  }
  title:
    | string
    | {
        value: string
      }
  message: {
    value: string
  }
  buttons: any
  /**
   * The four props `IDialogActionsProps` documents, once the reducer has
   * stored them. Optional here because every dialog but `confirmOrder` sends
   * none of them, and `closeDialog` rebuilds this object without them.
   */
  details?: IDialogDetails
  hidePrices?: boolean
  currency?: string
  currencyCode?: string
}

/**
 * Every checkout-rule name cinnamon can name, across two of the three
 * families that share the word `checkoutRules`.
 *
 * The camelCase members are the **validation** family — cart minimums,
 * maximums, returnables. They are the keys of `configuration.checkoutRules`
 * and the `ruleType` argument `checkoutSessionRule` and `acceptedCheckoutRule`
 * take.
 *
 * The two SCREAMING_SNAKE members are the **rule-document** family: what a
 * storefront may *offer* a shopper, read through `getCheckoutRule` and written
 * back as a selection by `setCheckoutRuleSession`. They live here because they
 * type the same `ruleType` argument on the wire, where the schema declares it
 * as a bare `String!` and would accept any typo.
 *
 * ⚠️ **Never put a SCREAMING_SNAKE member into `configuration.checkoutRules`.**
 * That map (`ICheckoutRules` in `graphQL/queries/getSession/types.ts`) is a
 * closed set of keys, each of which has a handler; an unhandled key disables
 * that storefront's rule enforcement outright (COM-736). The two families
 * share this enum and nothing else.
 *
 * ⚠️ **Adding a member widens `getFailedCheckoutRules`.** It filters a
 * `createOrder` rejection's `extensions.extras` against `Object.values` of
 * this enum, so a rejection naming a new member now reaches
 * `ordersSlice.failedCheckoutRules` where it was dropped before. That is the
 * intent for the two below — COM-950 is the reader — but it makes any addition
 * here a behaviour change in the write path, not only a vocabulary one.
 */
export enum CheckoutRules {
  CART_CONDITION_PENDING_MAX_QTY = 'cartConditionPendingMaxQty',
  CART_CONDITION_PENDING_MIN_QTY = 'cartConditionPendingMinQty',
  CART_CONDITION_PENDING_MAX_AMOUNT = 'cartConditionPendingMaxAmount',
  CART_CONDITION_PENDING_MIN_AMOUNT = 'cartConditionPendingMinAmount',
  CART_CONDITION_PENDING_USER_VALIDATION = 'cartConditionPendingUserValidation',
  CART_CONDITION_PENDING_MIN_ADDITIONAL_MEASUREMENT = 'cartConditionPendingMinAdditionalMeasurement',
  CART_CONDITION_PENDING_MAX_ADDITIONAL_MEASUREMENT = 'cartConditionPendingMaxAdditionalMeasurement',
  CART_WARNING_CHECK_RETURNABLES = 'cartWarningCheckReturnables',
  ORDER_DAILY_LIMIT = 'orderDailyLimit',
  CART_CONDITION_CHECK_LINKED_PRODUCTS = 'cartConditionCheckLinkedProducts',
  /**
   * The calendar days a storefront offers. `getCheckoutRule` returns them as
   * `YYYY-MM-DD` strings; a shopper's pick is written back through
   * `setCheckoutRuleSession` and read off `session.checkoutRules`.
   */
  DELIVERY_DATES = 'DELIVERY_DATES',
  /**
   * The payment methods a storefront offers, as backend enum values (`CASH`,
   * `CREDIT`, `TRANSFER`, `YALO_PAGO`). A tenant maps them to shopper-facing
   * words in config; cinnamon never translates one.
   *
   * The member is plural here and on the wire. The storefront's own enum
   * spells the member `PAYMENT_METHOD` with the value `'PAYMENT_METHODS'` —
   * the value is what travels, so this matches.
   */
  PAYMENT_METHODS = 'PAYMENT_METHODS',
}

/**
 * The rule-document members of `CheckoutRules`, and only those.
 *
 * `getCheckoutRule` and `setCheckoutRuleSession` accept these two alone.
 * Server-side, `ApplyRules` is a map keyed on them, so a camelCase validation
 * rule sent to either operation reaches an `undefined` handler and comes back
 * as a 500 — and the wire cannot reject it, because the schema declares
 * `ruleType` as a bare `String!`.
 *
 * Typing those operations with the whole enum would leave that a runtime
 * failure. This makes it a compile error at the call site instead.
 *
 * Deliberately **not** used for `IConfig.checkoutRuleRoutes`, which is keyed
 * on the full enum on purpose: it maps a *rejected* rule to the screen that
 * fixes it, and a rejection can name any rule the engine enforces.
 */
export type CheckoutRuleDocumentType =
  | CheckoutRules.DELIVERY_DATES
  | CheckoutRules.PAYMENT_METHODS

/**
 * Every member of `CheckoutRuleDocumentType`, as values rather than a type.
 *
 * The union says what a call site may pass. This says what a loop may walk,
 * and `checkoutSlice` needs that: it seeds one entry of derived UI flags per
 * rule type at boot. A `Partial` record cannot do the job. A config binding
 * that navigates to a missing key resolves to `undefined`, and `undefined`
 * reads as "show the empty-state message" before any fetch has run.
 *
 * `EveryDocumentTypeListed` fails the build when the union gains a member and
 * this array does not. Same guard shape as `EveryPanelStatusIsReachable` in
 * `utils/orderConfirmationPanel.ts`.
 *
 * ⚠️ Keep `as const satisfies`. An annotation (`: readonly
 * CheckoutRuleDocumentType[]`) widens each element back to the union, which
 * makes `(typeof …)[number]` the whole union and the guard below vacuous.
 */
export const CHECKOUT_RULE_DOCUMENT_TYPES = [
  CheckoutRules.DELIVERY_DATES,
  CheckoutRules.PAYMENT_METHODS,
] as const satisfies readonly CheckoutRuleDocumentType[]

type AssertNever<T extends never> = T
type EveryDocumentTypeListed = AssertNever<
  Exclude<
    CheckoutRuleDocumentType,
    (typeof CHECKOUT_RULE_DOCUMENT_TYPES)[number]
  >
>

export enum AlertConstrains {
  cartConditionPendingMinQty = 'cartConditionPendingMinQty',
  cartConditionPendingMinAmount = 'cartConditionPendingMinAmount',
  cartConditionPendingMaxAmount = 'cartConditionPendingMaxAmount',
  cartConditionPendingMaxQty = 'cartConditionPendingMaxQty',
  creditLimitMaxAmount = 'creditLimitMaxAmount',
}
export type IAlertsStatus = Record<AlertConstrains, { isActive: boolean }>

export interface IAlert
  extends Pick<
    SnackbarProps,
    | 'open'
    | 'message'
    | 'title'
    | 'origin'
    | 'topOffset'
    | 'bottomOffset'
    | 'showIcon'
    | 'showCloseButton'
    | 'closeOnClick'
    | 'autoHideDuration'
    | 'severity'
    | 'variant'
    | 'color'
    | 'size'
    | 'align'
  > {
  key?: string
}

export type LoaderConfig = string | { name: string; delay: number }
export interface IParams
  extends Record<string, string | UnresolvedRecord | undefined> {
  location?: {
    pathname: string
  }
}

export enum SessionNotifications {
  STOCK_AVAILABLE = 'STOCK_AVAILABLE',
  AUTHENTICATION_NEEDED = 'AUTHENTICATION_NEEDED',
  SESSION_CREATED = 'SESSION_CREATED',
  ORDER_REMINDER = 'ORDER_REMINDER',
  ABANDONED_CART = 'ABANDONED_CART',
}

export type SessionNotificationsArray = Array<{
  type: SessionNotifications
  enabled: boolean
}>

export interface ISliceDefault {
  loaders: string[]
  loading: boolean
  loadingDelay: number
  config?: IConfig
  sessionId?: string
  storeName?: string
  modals?: IModals
  dialogs?: IDialog
  sessionData?: SessionDataArgs
  validators: any[]
  errorMessage?: string | null
  currentModalItem?: string
  globalError: boolean
  showErrorTitle: boolean
  excludedUiComponents: string[]
  cartAlerts: IAlertsStatus
  params: IParams
  secondaryLoader?: boolean
  showRecommendations: boolean
  showSuggestedItems: boolean
  alerts: IAlert[]
  onErrorCallback?: () => void
  sessionNotifications?: Record<SessionNotifications, boolean>
}

export enum LoadersType {
  INITIAL_DATA = 'INITIAL_DATA',
  STORE_CONFIG = 'STORE_CONFIG',
  CREATE_ORDER = 'CREATE_ORDER',
}

export type DefaultProps<T> = Partial<T> & {
  template?: string
  runOnLoad?: ICinnamonActions
  hideOnScroll?: boolean
  hideOnScrollOffset?: number
  reachBottomActions?: ICinnamonActions
  [prop: string]: any
}

export interface IGetConfigArgs {
  slug: string
  language?: Localization
}
